import styled from 'styled-components';
import type { UseFormRegister, FieldErrors, Path, FieldValues } from 'react-hook-form';

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text};
`;

const Input = styled.input<{ $hasError?: boolean }>`
  padding: ${({ theme }) => theme.spacing.md};
  border: 2px solid ${({ theme, $hasError }) =>
    $hasError ? theme.colors.error : theme.colors.gray.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  transition: all ${({ theme }) => theme.transitions.fast};
  width: 100%;
  min-height: 48px;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    font-size: 16px;
    padding: ${({ theme }) => theme.spacing.lg};
  }

  &:focus {
    outline: none;
    border-color: ${({ theme, $hasError }) =>
      $hasError ? theme.colors.error : theme.colors.primary};
    box-shadow: 0 0 0 3px ${({ theme, $hasError }) =>
      $hasError ? theme.colors.error : theme.colors.primary}20;
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.gray.dark};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.gray.light};
    cursor: not-allowed;
  }
`;

const Select = styled.select<{ $hasError?: boolean }>`
  padding: ${({ theme }) => theme.spacing.md};
  border: 2px solid ${({ theme, $hasError }) =>
    $hasError ? theme.colors.error : theme.colors.gray.medium};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  background-color: ${({ theme }) => theme.colors.white};
  transition: all ${({ theme }) => theme.transitions.fast};
  width: 100%;
  min-height: 48px;

  &:focus {
    outline: none;
    border-color: ${({ theme, $hasError }) =>
      $hasError ? theme.colors.error : theme.colors.primary};
  }

  &:disabled {
    background-color: ${({ theme }) => theme.colors.gray.light};
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.span`
  color: ${({ theme }) => theme.colors.error};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  margin-top: ${({ theme }) => theme.spacing.xs};
`;

interface SelectOption {
  value: string | number;
  label: string;
}

interface BaseFormFieldProps<T extends FieldValues> {
  name: Path<T>;
  label: string;
  register: UseFormRegister<T>;
  errors: FieldErrors<T>;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
}

interface InputFormFieldProps<T extends FieldValues> extends BaseFormFieldProps<T> {
  type?: 'text' | 'email' | 'password' | 'number' | 'date';
  inputMode?: 'text' | 'email' | 'numeric' | 'tel' | 'url';
  autoComplete?: string;
  autoCapitalize?: string;
  autoCorrect?: string;
}

interface SelectFormFieldProps<T extends FieldValues> extends BaseFormFieldProps<T> {
  type: 'select';
  options: SelectOption[];
  emptyOptionLabel?: string;
}

type FormFieldProps<T extends FieldValues> = InputFormFieldProps<T> | SelectFormFieldProps<T>;

export function FormField<T extends FieldValues>(props: FormFieldProps<T>) {
  const { name, label, register, errors, placeholder, disabled, required } = props;
  const error = errors[name];
  const hasError = !!error;
  const id = String(name);

  if (props.type === 'select') {
    const { options, emptyOptionLabel } = props;
    return (
      <FormGroup>
        <Label htmlFor={id}>
          {label}{required && ' *'}
        </Label>
        <Select
          {...register(name)}
          id={id}
          $hasError={hasError}
          disabled={disabled}
        >
          {emptyOptionLabel && (
            <option value="">{emptyOptionLabel}</option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </Select>
        {error && (
          <ErrorMessage>{error.message as string}</ErrorMessage>
        )}
      </FormGroup>
    );
  }

  const { type = 'text', inputMode, autoComplete, autoCapitalize, autoCorrect } = props;

  return (
    <FormGroup>
      <Label htmlFor={id}>
        {label}{required && ' *'}
      </Label>
      <Input
        {...register(name)}
        id={id}
        type={type}
        placeholder={placeholder}
        $hasError={hasError}
        disabled={disabled}
        inputMode={inputMode}
        autoComplete={autoComplete}
        autoCapitalize={autoCapitalize}
        autoCorrect={autoCorrect}
        spellCheck={type === 'email' || type === 'password' ? false : undefined}
      />
      {error && (
        <ErrorMessage>{error.message as string}</ErrorMessage>
      )}
    </FormGroup>
  );
}

export type { FormFieldProps, SelectOption };
